/**
 * Pipe segment — discretized into N finite-volume cells with staggered grid.
 *
 * Grid layout:
 *   face[0]  cell[0]  face[1]  cell[1]  ...  face[N-1]  cell[N-1]  face[N]
 *      |----------|----------|          |----------|----------|
 *     mdot[0]   P[0],h[0]  mdot[1]            P[N-1],h[N-1]  mdot[N]
 *
 * Cell centers: P, h, rho, T, x, c, mu
 * Cell faces: mdot (mass flow rate)
 */

import type { PipeSegment, CellState, FaceState } from '../types';
import { createCellState, createFaceState } from '../types';
import { propsFromPH_SI, propsFromPT_SI } from '../core/fluidAdapter';
import { admitCanonicalPipeGeometry } from '../core/inputAdmission';

/**
 * Create a pipe segment with N cells.
 *
 * @param id Unique pipe identifier
 * @param diameter Pipe inner diameter [m]
 * @param length Pipe length [m]
 * @param roughness Absolute roughness [m] (default 0.05mm = commercial steel)
 * @param inclination Angle from horizontal [rad] (positive = uphill)
 * @param nCells Number of cells (auto-calculated from length if not specified)
 * @param P_init Initial pressure [Pa]
 * @param T_init Initial temperature [K]
 * @param mdot_init Initial mass flow rate [kg/s]
 */
export function createPipe(
  id: string,
  diameter: number,
  length: number,
  roughness: number = 0.00005,
  inclination: number = 0,
  nCells?: number,
  P_init: number = 101325,
  T_init: number = 293.15,
  mdot_init: number = 0,
): PipeSegment {
  // Canonical-SI construction is the common boundary for browser-built,
  // predefined and worker-restored pipes. Never turn invalid geometry into a
  // plausible area/Reynolds number downstream.
  const admittedGeometry = admitCanonicalPipeGeometry(id, diameter, roughness);
  diameter = admittedGeometry.diameter;
  roughness = admittedGeometry.roughness;

  // Auto cell count based on length
  const n = nCells ?? autoCellCount(length);
  const dx = length / n;
  const area = Math.PI / 4 * diameter * diameter;

  // Initialize all cells with uniform conditions
  const initProps = propsFromPH_SI(P_init, enthalpyFromPT(P_init, T_init));

  const cells: CellState[] = [];
  const cells_old: CellState[] = [];
  for (let i = 0; i < n; i++) {
    const cell: CellState = {
      P: P_init,
      h: initProps.h,
      rho: initProps.rho,
      T: initProps.T,
      u_int: initProps.u_int,
      x: initProps.x,
      c: initProps.c,
      mu: initProps.mu,
      cp: initProps.cp,
      phase: initProps.phase,
    };
    cells.push(cell);
    cells_old.push({ ...cell });
  }

  const faces: FaceState[] = [];
  const faces_old: FaceState[] = [];
  for (let i = 0; i <= n; i++) {
    const u = mdot_init > 0 ? mdot_init / (initProps.rho * area) : 0;
    const face: FaceState = { mdot: mdot_init, u };
    faces.push(face);
    faces_old.push({ ...face });
  }

  return {
    id, diameter, length, roughness, inclination, wallHeatFlux: 0,
    nCells: n, dx, area,
    cells, faces, cells_old, faces_old,
  };
}

/**
 * Save current state as "old" for the next time step.
 */
export function savePipeState(pipe: PipeSegment): void {
  for (let i = 0; i < pipe.nCells; i++) {
    Object.assign(pipe.cells_old[i], pipe.cells[i]);
  }
  for (let i = 0; i <= pipe.nCells; i++) {
    Object.assign(pipe.faces_old[i], pipe.faces[i]);
  }
}

/**
 * Update derived properties for all cells from (P, h).
 */
export function updatePipeProperties(pipe: PipeSegment): void {
  for (let i = 0; i < pipe.nCells; i++) {
    const cell = pipe.cells[i];
    // Guard: skip if P or h are NaN/Infinity — keep old values
    if (!isFinite(cell.P) || !isFinite(cell.h)) continue;
    // Clamp P and h to IAPWS-97 valid range
    cell.P = Math.max(611.657, Math.min(100e6, cell.P));
    cell.h = Math.max(100, Math.min(5e6, cell.h));
    try {
      const props = propsFromPH_SI(cell.P, cell.h);
      // Under-relax density to prevent oscillation while preserving mass conservation.
      // Unlike a hard limiter, relaxation converges to the correct EOS value.
      const rho_new = props.rho;
      const rho_old = cell.rho;
      // High relaxation for single-phase liquid (density changes are small and well-behaved).
      // Low relaxation only needed for two-phase transitions.
      const isTwoPhase = cell.x >= 0 && cell.x <= 1;
      const alpha_rho = isTwoPhase ? 0.3 : 0.9;
      if (rho_old > 0 && isFinite(rho_new) && rho_new > 0) {
        cell.rho = Math.max(0.01, rho_old + alpha_rho * (rho_new - rho_old));
      } else {
        cell.rho = Math.max(0.01, rho_new);
      }
      cell.T = props.T;
      cell.u_int = props.u_int;
      cell.x = props.x;
      cell.c = props.c;
      cell.mu = props.mu;
      cell.cp = props.cp;
      cell.phase = props.phase;
    } catch {
      // Property lookup failed — keep previous values
    }
  }

  // Update face velocities from mass flow rates
  for (let i = 0; i <= pipe.nCells; i++) {
    const rho_face = faceRho(pipe, i);
    pipe.faces[i].u = rho_face > 0 ? pipe.faces[i].mdot / (rho_face * pipe.area) : 0;
  }
}

/**
 * Interpolate density at a face from adjacent cells.
 */
export function faceRho(pipe: PipeSegment, faceIdx: number): number {
  if (faceIdx === 0) return pipe.cells[0].rho;
  if (faceIdx === pipe.nCells) return pipe.cells[pipe.nCells - 1].rho;
  return 0.5 * (pipe.cells[faceIdx - 1].rho + pipe.cells[faceIdx].rho);
}

/**
 * Auto-select cell count based on pipe length.
 */
function autoCellCount(length: number): number {
  if (length < 5) return 2;
  if (length < 50) return 5;
  if (length < 500) return 10;
  return 20;
}

/**
 * Estimate enthalpy from P and T for initialization.
 */
function enthalpyFromPT(P_Pa: number, T_K: number): number {
  try {
    const T_clamped = Math.max(273.16, Math.min(623.15, T_K)); // IAPWS range
    const P_clamped = Math.max(611.657, Math.min(100e6, P_Pa)); // IAPWS range
    const props = propsFromPT_SI(P_clamped, T_clamped);
    return props.h;
  } catch {
    // Fallback: use cp × ΔT for liquid, or estimate for steam
    if (T_K > 373.15) {
      // Steam: h ≈ 2500 kJ/kg + cp_steam × (T - 100°C), convert to J/kg
      return (2500 + 2.0 * (T_K - 373.15)) * 1000;
    }
    return 4182 * (T_K - 273.15);
  }
}
